import { z } from 'zod'

/**
 * The three kinds of time this codebase moves, as three different types.
 *
 * `docs/event-scheduling.md` exists because a **calendar day**, a **wall-clock
 * time at the venue** and a **real instant** are not the same thing. Until now
 * the type system knew all three as `string`, so every one of these compiled:
 *
 * ```ts
 * formatDate(event.createdAt)          // an instant formatted as maybe-a-day
 * formatDate(event.startsAt)           // a floating time in the viewer's zone
 * utcDay(new Date(event.startsAt))     // which day is a floating time on?
 * ```
 *
 * All three are bugs that actually shipped. The May 13/May 14 rendering, a phase
 * frozen on the morning of its own event, an event dropping off the list from
 * its own midnight, a 10pm reception shown at 3pm, a date range leaking the
 * server's offset, and a converter emitting a day into a field declared
 * `.datetime()` — that last one broke the venue calendar and reached `main`,
 * because both sides were `string` and there was nothing to notice.
 *
 * Branding makes the distinction visible to the compiler. It costs a nominal
 * type and one construction site per kind; it buys an error at the point of the
 * mistake instead of a review three commits later.
 *
 * **Constructing one asserts a format**, which is why this file is the only
 * place allowed to (see the scoped override in `lib/eslint.config.js`): a brand
 * cannot be attached without telling the compiler something it cannot check.
 * Every constructor here validates first and throws otherwise, so the assertion
 * is only ever made about a value that has been checked.
 */
/**
 * The brand is a string-literal property, not a `unique symbol`.
 *
 * A `unique symbol` gives a stronger nominal type, and `asset-ref.ts` uses one
 * — but this module is reachable both as `@lib/*` source and through the built
 * `@cse/lib`, and a `unique symbol` declared in a module that lands in the
 * program twice gets two identities. That produces
 * "Two different types with this name exist, but they are unrelated" between
 * two files that agree perfectly. A literal-keyed brand is structural, so it
 * survives being resolved twice.
 *
 * Nothing is lost in practice: `string & { readonly __temporal: ... }` is still
 * not constructible without an assertion, and the assertions all live here.
 */

/**
 * A calendar day: `2027-05-14`.
 *
 * Not a moment. It has no time and no zone, and formatting it in one shows the
 * previous day to everybody west of Greenwich.
 */
export type CalendarDay = string & { readonly __temporal: 'CalendarDay' }

/**
 * A wall-clock time at the venue: the `HH:MM` inside `2026-10-17T22:00:00.000Z`.
 *
 * The `Z` is a lie of convenience — the value is 22:00 *at the venue*, on
 * whatever day it is applied to, and is never converted. Turning it into a real
 * instant needs the venue's own clock; that is what `Venue.timezone` is for.
 */
export type WallClock = string & { readonly __temporal: 'WallClock' }

/** A real moment in time, as an ISO 8601 string with a zone that means it. */
export type Instant = string & { readonly __temporal: 'Instant' }

const CALENDAR_DAY_RE = /^\d{4}-\d{2}-\d{2}$/

/** True if the string is a `yyyy-MM-dd` naming a day that exists. */
function looksLikeDay(value: string): boolean {
  if (!CALENDAR_DAY_RE.test(value)) return false
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

/** True if the string parses as an ISO datetime. */
function looksLikeDateTime(value: string): boolean {
  return !Number.isNaN(new Date(value).getTime()) && value.length > 10
}

/**
 * A checked `yyyy-MM-dd`.
 *
 * It used to accept a full ISO datetime and keep the day, so that a field an
 * older release still sent as a midnight timestamp could be pointed at it. Both
 * halves of every environment send days now, and a tolerance nobody emits into
 * is not neutral: it silently accepts a timestamp forever, and the thing it
 * accepts is precisely the day-pretending-to-be-an-instant this type exists to
 * rule out. `DEPLOY_NOTES.md` carried the removal.
 */
export function calendarDay(value: string): CalendarDay {
  if (!looksLikeDay(value)) {
    throw new TypeError(`Not a calendar day: ${JSON.stringify(value)}`)
  }
  return value as CalendarDay
}

/**
 * A calendar day when the string is one, otherwise null.
 *
 * For the places a value arrives from outside and may be empty or junk — a
 * `<input type="date">`, a URL query parameter. The throwing {@link calendarDay}
 * is for values that are already known good.
 */
export function toCalendarDay(value: string | null | undefined): CalendarDay | null {
  if (value === null || value === undefined || value === '') return null
  try {
    return calendarDay(value)
  } catch {
    return null
  }
}

/** The calendar day a `Date` falls on, in UTC. */
export function calendarDayOf(value: Date): CalendarDay {
  return calendarDay(value.toISOString().slice(0, 10))
}

/** A checked wall-clock timestamp — an ISO datetime whose time is the venue's. */
export function wallClock(value: string): WallClock {
  if (!looksLikeDateTime(value)) {
    throw new TypeError(`Not a wall-clock timestamp: ${JSON.stringify(value)}`)
  }
  return value as WallClock
}

/** A wall-clock timestamp from a `Date` whose UTC fields hold the venue's clock. */
export function wallClockOf(value: Date): WallClock {
  return wallClock(value.toISOString())
}

/** A checked real instant. */
export function instant(value: string): Instant {
  if (!looksLikeDateTime(value)) {
    throw new TypeError(`Not an instant: ${JSON.stringify(value)}`)
  }
  return value as Instant
}

/** A real instant from a `Date`, which is what a `Date` actually is. */
export function instantOf(value: Date): Instant {
  return value.toISOString() as Instant
}

/**
 * A calendar day on the wire.
 *
 * Strict again, now that no release sends the midnight timestamp it used to
 * tolerate. **One gate, not two.** An earlier version paired a `.refine` that
 * decided with a `.transform` that decided again, and they drifted: the refine
 * accepted anything whose first ten characters looked like a day, while the
 * constructor only sliced a value that parsed whole — so `'2027-05-14T'` passed
 * the refine and threw a raw `TypeError` out of `safeParse`. A pipe that cannot
 * report failure turns a bad request into a 500, and in the browser it escapes
 * the network-boundary parse entirely. `.date()` decides, and the constructor
 * only brands what it passed.
 */
export const calendarDaySchema: z.ZodType<CalendarDay> = z
  .string()
  .date('Expected a calendar day (yyyy-MM-dd)')
  .transform(calendarDay)

/** A calendar day that may be absent. */
export const nullableCalendarDaySchema = calendarDaySchema.nullable()

/** A wall-clock timestamp on the wire. */
export const wallClockSchema: z.ZodType<WallClock> = z
  .string()
  .datetime()
  .transform(wallClock)

/** A real instant on the wire. */
export const instantSchema: z.ZodType<Instant> = z.string().datetime().transform(instant)
